import {
  channelMetaDataKeys,
  userMetaDataKeys,
} from '@inbox-demo-nx/constants';
import {
  ScenarioData,
  SendApiRequest,
  SingleContentScenarioMessage,
} from '@inbox-demo-nx/types';
import {
  convertMessagesToPayloadArray,
  convertScenarioMessageTimestamp,
  sendScenarioMessage,
} from '@inbox-demo-nx/utils-scenario';
import axios from 'axios';

export default async function resetUser({
  userId,
  scenario,
  sendApiRequest,
}: {
  userId: string;
  scenario: ScenarioData;
  sendApiRequest: SendApiRequest;
}) {
  try {
    await sendApiRequest({ type: 'deleteAllGroupChannels', data: { userId } });

    for (const channelData of scenario.channels) {
      const {
        name,
        isFrozen,
        members,
        customType,
        states,
        coverUrl = '',
        initialReadAt,
      } = channelData;
      const channel = await sendApiRequest({
        type: 'createGroupChannel',
        data: {
          name,
          is_distinct: true,
          custom_type: customType,
          cover_url: coverUrl,
          user_ids: [userId, ...members],
        },
      });
      const channelUrl = channel.channel_url;

      const processedMessages = convertMessagesToPayloadArray(
        states.initial.messages
      );

      const initialReadAtTimestamp =
        initialReadAt === 0
          ? Date.now()
          : initialReadAt == null
          ? null
          : convertScenarioMessageTimestamp(initialReadAt);

      const [firstMessages, secondMessages] = processedMessages.reduce(
        ([firstList, secondList], cur) => {
          if (initialReadAtTimestamp == null) {
            firstList.push(cur);
            return [firstList, secondList];
          }
          if (cur.createdAt > initialReadAtTimestamp) {
            secondList.push(cur);
          } else {
            firstList.push(cur);
          }
          return [firstList, secondList];
        },
        [
          [] as SingleContentScenarioMessage[],
          [] as SingleContentScenarioMessage[],
        ]
      );

      await Promise.all(
        firstMessages.map((message) =>
          sendScenarioMessage(message, {
            myUserId: userId,
            channelMetaData: {},
            channelUrl,
            sendApiRequest,
          })
        )
      );

      if (initialReadAtTimestamp != null) {
        await sendApiRequest({
          type: 'markAsRead',
          data: {
            userId,
            channelUrl,
            timestamp: initialReadAtTimestamp,
          },
        });
      }

      await Promise.all(
        secondMessages.map((message) =>
          sendScenarioMessage(message, {
            myUserId: userId,
            channelMetaData: {},
            channelUrl,
            sendApiRequest,
          })
        )
      );

      await sendApiRequest({
        type: 'updateGroupChannelMetaData',
        data: {
          channelUrl,
          metaData: { [channelMetaDataKeys.state]: 'initial' },
        },
      });
      if (isFrozen) {
        await sendApiRequest({
          type: 'freezeGroupChannel',
          data: { channelUrl },
        });
      }
    }

    await sendApiRequest({
      type: 'updateUserMetaData',
      data: {
        userId,
        metadata: {
          [userMetaDataKeys.initializedVersion]: scenario.version,
          [userMetaDataKeys.scenario]: scenario.key,
          [userMetaDataKeys.language]: scenario.defaultLanguage,
        },
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.config) {
      const { status, statusText, data, config } = error.response;
      const { url, method, data: configData, params } = config;

      // eslint-disable-next-line no-throw-literal
      throw {
        status,
        statusText,
        data,
        config: { method, url, data: configData, params },
      };
    }
    throw error;
  }
}
